import { useCallback, useEffect, useRef } from "react";
import config from "app.config";
import { useFeatureFlag } from "hooks/FlagHooks";
import { useLanguage } from "mocks/LanguageHooks";
import { switchWeglotLang } from "utils/weglot";
import { ScriptLoader } from "./ScriptLoader";

export function WeglotLoader() {
  const { data: weglotAvailable } = useFeatureFlag("weglot");
  const [currentLang] = useLanguage();
  const currentLangRef = useRef(currentLang);
  currentLangRef.current = currentLang;

  const onLoadScript = useCallback(() => {
    // Initialize Weglot after script is loaded
    if (window.Weglot && typeof window.Weglot.initialize === "function") {
      window.Weglot.initialize({
        api_key: config.weglotKey,
        hide_switcher: true,
        cache: true,
        auto_switch_fallback: "en",
      });
      // Weglot persists its own selected language and can restore a stale one that drifts
      // from the app's language (which mismatches <html lang>, the text, and layout
      // direction). Force it to the app's language once it's fully initialized. Weglot's
      // `initialized` flag lags the script load, so poll briefly until it's ready.
      let attempts = 0;
      const syncWhenReady = () => {
        if (window.Weglot?.initialized) {
          if (currentLangRef.current) {
            switchWeglotLang(currentLangRef.current);
          }
        } else if (attempts++ < 20) {
          window.setTimeout(syncWhenReady, 100);
        }
      };
      syncWhenReady();
    }
  }, []);

  // Keep Weglot following the app's language when it changes after the initial sync.
  useEffect(() => {
    if (weglotAvailable && currentLang && window.Weglot?.initialized) {
      switchWeglotLang(currentLang);
    }
  }, [weglotAvailable, currentLang]);

  if (!weglotAvailable) {
    return null;
  }

  return (
    <ScriptLoader
      src="https://cdn.weglot.com/weglot.min.js"
      id="weglot-script"
      async={true}
      onLoad={onLoadScript}
    />
  );
}

// Add TypeScript declaration for Weglot
declare global {
  interface Window {
    Weglot?: {
      initialize: (config: {
        api_key: string;
        hide_switcher?: boolean;
        cache?: boolean;
        auto_switch_fallback?: string;
      }) => void;
      initialized?: boolean;
      [key: string]: unknown;
    };
  }
}
